import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ContentCopy as CopyIcon,
  Error as ErrorIcon,
  Launch as LaunchIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Storefront as StoreIcon,
  TrendingDown as DownIcon,
  TrendingUp as UpIcon,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import service, {
  ConversaoData,
  RankingComerciante,
  RankingUtilizador,
  TransparenciaData,
  WAUResult,
} from '../../services/marketing-metricas.service';
import metaService, {
  MetaAdSummary,
  MetaCampaign,
  MetaConfig,
  MetaIGData,
} from '../../services/marketing-meta.service';
import windsorService, { WindsorMetricsResult, WindsorSyncLog } from '../../services/marketing-windsor.service';
import { PageHeader, StatCard } from '../../components/Marketing';
import {
  InsightsOutlined,
  GroupsOutlined,
  PaidOutlined,
  ReceiptLongOutlined,
  StarsOutlined,
  StorefrontOutlined,
  CampaignOutlined,
  EmojiEventsOutlined,
  HandshakeOutlined,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  AdsClick as AdsClickIcon,
  Visibility as VisibilityIcon,
  TouchApp as TouchAppIcon,
  Payments as PaymentsIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { todayCv } from '../../utils/date';

const formatCve = (v?: number | null) => (v == null ? '0' : Number(v).toLocaleString('pt-PT'));
const formatShortDate = (v: string) => {
  // v é um dia (DATE / 'YYYY-MM-DD') → ler em UTC, não no fuso do browser.
  const d = new Date(v);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export default function Metricas() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [wau, setWau] = useState<WAUResult | null>(null);
  const [transp, setTransp] = useState<TransparenciaData | null>(null);
  const [rankU, setRankU] = useState<RankingUtilizador[]>([]);
  const [rankC, setRankC] = useState<RankingComerciante[]>([]);
  const [conv, setConv] = useState<ConversaoData | null>(null);
  const [atrib, setAtrib] = useState<{ fontes: any[]; campanhas: any[]; total: number } | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Meta Ads state
  const [metaConfig, setMetaConfig] = useState<MetaConfig | null>(null);
  const [metaSummary, setMetaSummary] = useState<MetaAdSummary | null>(null);
  const [metaCampaigns, setMetaCampaigns] = useState<MetaCampaign[]>([]);
  const [metaIg, setMetaIg] = useState<MetaIGData | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaConfigOpen, setMetaConfigOpen] = useState(false);
  const [metaPreset, setMetaPreset] = useState('last_30d');

  // Windsor state
  const [windsorIg, setWindsorIg] = useState<WindsorMetricsResult | null>(null);
  const [windsorAds, setWindsorAds] = useState<WindsorMetricsResult | null>(null);
  const [windsorPrevIg, setWindsorPrevIg] = useState<WindsorMetricsResult | null>(null);
  const [windsorPrevAds, setWindsorPrevAds] = useState<WindsorMetricsResult | null>(null);
  const [windsorSyncLog, setWindsorSyncLog] = useState<WindsorSyncLog[]>([]);
  const [windsorSyncing, setWindsorSyncing] = useState(false);
  const [windsorDays, setWindsorDays] = useState(30);

  // Resumo Diário state
  const [resumo, setResumo] = useState<{ text: string; gerado_em: string; meta: any } | null>(null);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [resumoError, setResumoError] = useState<string | null>(null);

  // Funil state
  const [funil, setFunil] = useState<{
    semana_atual: {
      registos: number; kyc_aprovado: number; kyc_pendente: number;
      primeira_transacao: number; taxa_kyc_pct: number | null; taxa_activacao_pct: number | null;
    };
    historico_firestore: any[];
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, t, u, c, cv, at, fn] = await Promise.all([
        service.wau(12).catch(() => null),
        service.transparencia().catch(() => null),
        service.rankingUtilizadores(10).catch(() => []),
        service.rankingComerciantes(10).catch(() => []),
        service.conversao().catch(() => null),
        service.atribuicao().catch(() => null),
        service.funil().catch(() => null),
      ]);
      setWau(w);
      setTransp(t);
      setRankU(u);
      setRankC(c);
      setConv(cv);
      setAtrib(at);
      setFunil(fn);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMeta = useCallback(async () => {
    setMetaLoading(true);
    setMetaError(null);
    try {
      const cfg = await metaService.getConfig().catch(() => null);
      setMetaConfig(cfg);
      if (cfg?.access_token_set && cfg?.ad_account_id) {
        const [summary, camps] = await Promise.all([
          metaService.getSummary(metaPreset).catch(() => null),
          metaService.getCampaigns(metaPreset, 15).catch(() => ({ data: [] })),
        ]);
        setMetaSummary(summary);
        setMetaCampaigns(camps.data);
      }
      if (cfg?.access_token_set && cfg?.ig_user_id) {
        const ig = await metaService.getInstagram(9).catch(() => null);
        setMetaIg(ig);
      }

    } catch (e: any) {
      setMetaError(e.message);
    } finally {
      setMetaLoading(false);
    }
  }, [metaPreset]);

  const loadWindsorData = useCallback(async (days: number) => {
    // Dias de calendário de CV, com aritmética em UTC (independente do browser).
    const to = todayCv();
    const fromDate = new Date(to + 'T00:00:00Z');
    fromDate.setUTCDate(fromDate.getUTCDate() - days);
    const from = fromDate.toISOString().slice(0, 10);
    const prevToDate = new Date(fromDate);
    prevToDate.setUTCDate(prevToDate.getUTCDate() - 1);
    const prevTo = prevToDate.toISOString().slice(0, 10);
    const prevFromDate = new Date(prevToDate);
    prevFromDate.setUTCDate(prevFromDate.getUTCDate() - days);
    const prevFrom = prevFromDate.toISOString().slice(0, 10);
    const [wIg, wAds, wPrevIg, wPrevAds, wLog] = await Promise.all([
      windsorService.getMetrics('instagram', undefined, from, to).catch(() => null),
      windsorService.getMetrics('facebook', undefined, from, to).catch(() => null),
      windsorService.getMetrics('instagram', undefined, prevFrom, prevTo).catch(() => null),
      windsorService.getMetrics('facebook', undefined, prevFrom, prevTo).catch(() => null),
      windsorService.getSyncLog().catch(() => []),
    ]);
    setWindsorIg(wIg);
    setWindsorAds(wAds);
    setWindsorPrevIg(wPrevIg);
    setWindsorPrevAds(wPrevAds);
    setWindsorSyncLog(wLog);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 4) loadMeta();
  }, [tab, loadMeta]);

  useEffect(() => {
    if (tab === 4) loadWindsorData(windsorDays);
  }, [tab, windsorDays, loadWindsorData]);

  const loadResumo = useCallback(async () => {
    setResumoLoading(true);
    setResumoError(null);
    try {
      const d = await service.resumoDiario();
      setResumo({ text: d.resumo, gerado_em: d.gerado_em, meta: d.meta });
    } catch (e: any) {
      setResumoError(e?.response?.data?.error || e.message || 'Erro desconhecido');
    } finally {
      setResumoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 5 && !resumo && !resumoLoading && !resumoError) loadResumo();
  }, [tab, resumo, resumoLoading, resumoError, loadResumo]);

  const wauChartData = wau?.semanas.map((s) => ({
    label: formatShortDate(s.semana_inicio),
    wau: s.wau,
  })) || [];

  const copyTransparencia = async () => {
    if (!transp) return;
    const t = `🎉 Pagali em números — ${new Date().toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', month: 'long', year: 'numeric' })}

💰 ${formatCve(transp.cashback_cve)} CVE devolvidos em cashback
👥 ${formatCve(transp.utilizadores)} utilizadores ativos
💳 ${formatCve(transp.transacoes)} transações
⭐ ${formatCve(transp.pontos_distribuidos)} pontos distribuídos
🏪 ${transp.comerciantes_ativos} parceiros

#Pagali #CaboVerde`;
    try {
      await navigator.clipboard.writeText(t);
      setCopiedText('Texto copiado!');
      setTimeout(() => setCopiedText(null), 2000);
    } catch {
      setCopiedText('Erro ao copiar');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Métricas & KPIs"
        subtitle="WAU, transparência pública, rankings e conversão por canal"
        icon={<InsightsOutlined />}
        actions={
          <Stack direction="row" spacing={1}>
            {tab === 4 && (
              <Button
                startIcon={<SettingsIcon />}
                onClick={() => setMetaConfigOpen(true)}
                variant="outlined"
              >
                Configurar
              </Button>
            )}
            <Button
              startIcon={<RefreshIcon />}
              onClick={tab === 4 ? loadMeta : load}
              disabled={tab === 4 ? metaLoading : loading}
            >
              Atualizar
            </Button>
          </Stack>
        }
      />

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="WAU" />
          <Tab label="Transparência" />
          <Tab label="Rankings" />
          <Tab label="Conversão" />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <FacebookIcon sx={{ fontSize: 16 }} />
                <InstagramIcon sx={{ fontSize: 16 }} />
                <span>Rede Social</span>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                <span>Resumo Diário</span>
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {loading && !wau ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* TAB 0: WAU */}
          {tab === 0 && wau && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
                <StatCard
                  icon={<GroupsOutlined />}
                  label="WAU (semana atual)"
                  value={formatCve(wau.total_atual)}
                  color="primary"
                  delta={wau.delta_pct}
                  deltaLabel="vs semana anterior"
                />
                <StatCard
                  icon={<InsightsOutlined />}
                  label="Semanas analisadas"
                  value={wauChartData.length}
                  color="info"
                />
                <StatCard
                  icon={<UpIcon />}
                  label="Pico"
                  value={formatCve(Math.max(...wauChartData.map((d) => d.wau), 0))}
                  color="success"
                />
                <StatCard
                  icon={<DownIcon />}
                  label="Mínimo"
                  value={formatCve(Math.min(...wauChartData.map((d) => d.wau), 0))}
                  color="warning"
                />
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <b>Passo 20 do plano</b>: a métrica mais importante. Se o WAU cresce, o Pagali cresce.
                Não downloads, não seguidores — <b>utilizadores ativos por semana</b>.
              </Alert>

              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Evolução WAU
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Últimas {wauChartData.length} semanas · utilizadores únicos ativos por semana
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ width: '100%', height: 360 }}>
                  <ResponsiveContainer width="100%" height={300} debounce={50}>
                    <AreaChart data={wauChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorWau" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="label" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="wau" stroke="#7c3aed" fill="url(#colorWau)" strokeWidth={2.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>

              {funil && <FunilCard funil={funil} />}
            </>
          )}

          {/* TAB 1: TRANSPARÊNCIA */}
          {tab === 1 && transp && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <b>Passo 16</b>: publique mensalmente estes números para construir autoridade e
                confiança. Use o botão "Copiar post" para texto formatado.
              </Alert>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 3, mb: 3 }}>
                <StatCard icon={<PaidOutlined />} label="Cashback devolvido" value={`${formatCve(transp.cashback_cve)} CVE`} color="warning" />
                <StatCard icon={<GroupsOutlined />} label="Utilizadores ativos" value={formatCve(transp.utilizadores)} color="primary" />
                <StatCard icon={<ReceiptLongOutlined />} label="Transações" value={formatCve(transp.transacoes)} color="info" />
                <StatCard icon={<StarsOutlined />} label="Pontos distribuídos" value={formatCve(transp.pontos_distribuidos)} color="secondary" />
                <StatCard icon={<StorefrontOutlined />} label="Parceiros" value={transp.comerciantes_ativos} color="success" />
              </Box>

              <Paper
                sx={{
                  p: { xs: 3, md: 5 },
                  mb: 3,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
                }}
              >
                <Typography variant="h3" fontWeight={800} mb={1} textAlign="center" sx={{ letterSpacing: -0.5 }}>
                  🎉 Pagali em números
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }} textAlign="center" mb={4}>
                  Preview do post · atualizado em tempo real
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: { xs: 3, md: 4 } }}>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, fontSize: 11, fontWeight: 700 }}>
                      💰 CASHBACK DEVOLVIDO
                    </Typography>
                    <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1 }}>
                      {formatCve(transp.cashback_cve)} CVE
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, fontSize: 11, fontWeight: 700 }}>
                      👥 UTILIZADORES
                    </Typography>
                    <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1 }}>
                      {formatCve(transp.utilizadores)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mt: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, fontSize: 11, fontWeight: 700 }}>
                      💳 TRANSAÇÕES
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                      {formatCve(transp.transacoes)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, fontSize: 11, fontWeight: 700 }}>
                      ⭐ PONTOS
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                      {formatCve(transp.pontos_distribuidos)}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, fontSize: 11, fontWeight: 700 }}>
                      🏪 PARCEIROS
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                      {transp.comerciantes_ativos}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Button variant="contained" size="large" startIcon={<CopyIcon />} onClick={copyTransparencia}>
                  Copiar post para redes sociais
                </Button>
                {copiedText && <Chip label={copiedText} color="success" />}
                <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
                  Endpoint público: <code>GET /v1/public/metricas/transparencia</code>
                </Typography>
              </Stack>
            </>
          )}

          {/* TAB 2: RANKINGS */}
          {tab === 2 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PeopleIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600} lineHeight={1.2}>Top 10 Utilizadores</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Passo 8 — competição social aumenta retenção
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Utilizador</TableCell>
                        <TableCell align="right">Trans.</TableCell>
                        <TableCell align="right">Pontos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankU.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                            Ainda sem dados. Começa a aparecer após primeiras transações com pontos.
                          </TableCell>
                        </TableRow>
                      ) : (
                        rankU.map((u, i) => (
                          <TableRow key={u.user_id} hover>
                            <TableCell>
                              <Chip
                                size="small"
                                label={`#${i + 1}`}
                                color={i === 0 ? 'warning' : i < 3 ? 'primary' : 'default'}
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {u.nome || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {u.email}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{u.num_transacoes}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCve(u.total_pontos)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, backgroundColor: 'success.main', color: 'success.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StoreIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600} lineHeight={1.2}>Top 10 Comerciantes</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Volume de vendas via wallet Pagali
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Comerciante</TableCell>
                        <TableCell align="right">Clientes</TableCell>
                        <TableCell align="right">Volume</TableCell>
                        <TableCell align="right">Ticket</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankC.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                            Sem transações wallet registadas ainda.
                          </TableCell>
                        </TableRow>
                      ) : (
                        rankC.map((c, i) => (
                          <TableRow key={c.entity_id} hover>
                            <TableCell>
                              <Chip
                                size="small"
                                label={`#${i + 1}`}
                                color={i === 0 ? 'warning' : i < 3 ? 'primary' : 'default'}
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>{c.entidade_nome}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {c.num_transacoes} transações
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{c.clientes_unicos}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCve(c.volume_cve)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatCve(Math.round(c.ticket_medio))}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}

          {/* TAB 4: SOCIAL ADS */}
          {tab === 4 && (
            <SocialAdsTab
              config={metaConfig}
              summary={metaSummary}
              campaigns={metaCampaigns}
              ig={metaIg}
              loading={metaLoading}
              error={metaError}
              preset={metaPreset}
              onPresetChange={setMetaPreset}
              onRefresh={loadMeta}
              onConfigure={() => setMetaConfigOpen(true)}
              wau={wau}
              windsorIg={windsorIg}
              windsorAds={windsorAds}
              windsorPrevIg={windsorPrevIg}
              windsorPrevAds={windsorPrevAds}
              windsorSyncLog={windsorSyncLog}
              windsorSyncing={windsorSyncing}
              windsorDays={windsorDays}
              onWindsorDaysChange={setWindsorDays}
              onWindsorSync={async () => {
                setWindsorSyncing(true);
                try {
                  await windsorService.triggerSync();
                  await loadWindsorData(windsorDays);
                } finally {
                  setWindsorSyncing(false);
                }
              }}
            />
          )}

          {/* TAB 3: CONVERSÃO */}
          {tab === 3 && conv && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
              <ConversionCard
                icon={<CampaignOutlined />}
                title="Campanhas SMS/Email"
                color="info"
                hasData={!!conv.campanhas && conv.campanhas.total > 0}
                emptyText="Ainda sem campanhas enviadas"
              >
                {conv.campanhas && (
                  <>
                    <Stack spacing={1.25} mb={2.5}>
                      <MetricRow label="Total destinatários" value={conv.campanhas.total} />
                      <MetricRow label="Enviados" value={conv.campanhas.enviados} color="success.main" />
                      <MetricRow label="Falhou" value={conv.campanhas.falhou} color="error.main" />
                      <MetricRow label="Opt-out" value={conv.campanhas.opt_out} color="text.secondary" />
                    </Stack>
                    <ProgressBar label="Taxa de entrega" value={conv.campanhas.taxa_entrega} />
                  </>
                )}
              </ConversionCard>

              <ConversionCard
                icon={<EmojiEventsOutlined />}
                title="Missões"
                color="warning"
                hasData={!!conv.missoes && conv.missoes.participantes > 0}
                emptyText="Ainda sem participação em missões"
              >
                {conv.missoes && (
                  <>
                    <Stack spacing={1.25} mb={2.5}>
                      <MetricRow label="Participantes" value={conv.missoes.participantes} />
                      <MetricRow label="Completaram" value={conv.missoes.completaram} color="success.main" />
                      <MetricRow label="Reclamaram" value={conv.missoes.reclamaram} color="primary.main" />
                    </Stack>
                    <Stack spacing={1.5}>
                      <ProgressBar label="Taxa de conclusão" value={conv.missoes.taxa_conclusao} />
                      <ProgressBar label="Taxa de reclamação" value={conv.missoes.taxa_reclamacao} />
                    </Stack>
                  </>
                )}
              </ConversionCard>

              <ConversionCard
                icon={<HandshakeOutlined />}
                title="Referral"
                color="success"
                hasData={!!conv.referral && conv.referral.total > 0}
                emptyText="Ainda sem convites aceites"
              >
                {conv.referral && (
                  <>
                    <Stack spacing={1.25} mb={2.5}>
                      <MetricRow label="Convites" value={conv.referral.total} />
                      <MetricRow label="Aceites" value={conv.referral.aceites} color="success.main" />
                    </Stack>
                    <ProgressBar label="Taxa de aceitação" value={conv.referral.taxa_aceitacao} />
                  </>
                )}
              </ConversionCard>
            </Box>
          )}

          {/* TAB 5: RESUMO DIÁRIO */}
          {tab === 5 && (
            <ResumoDiarioTab
              resumo={resumo}
              loading={resumoLoading}
              error={resumoError}
              onRefresh={loadResumo}
            />
          )}

          {/* Atribuição UTM */}
          {tab === 3 && atrib && atrib.total > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Origem dos Registos (UTM) — {atrib.total} registos com atribuição
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>Fontes</Typography>
                  <Stack spacing={0.75}>
                    {atrib.fontes.map((f: any) => (
                      <Stack key={`${f.fonte}-${f.meio}`} direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{f.fonte}</Typography>
                          <Typography variant="caption" color="text.secondary">{f.meio}</Typography>
                        </Box>
                        <Chip label={f.registos} size="small" color="primary" variant="outlined" />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
                {atrib.campanhas?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>Campanhas</Typography>
                    <Stack spacing={0.75}>
                      {atrib.campanhas.map((c: any) => (
                        <Stack key={`${c.campanha}-${c.fonte}`} direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{c.campanha}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.fonte}</Typography>
                          </Box>
                          <Chip label={c.registos} size="small" color="secondary" variant="outlined" />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </>
      )}

      <MetaConfigDialog
        open={metaConfigOpen}
        config={metaConfig}
        onClose={() => setMetaConfigOpen(false)}
        onSaved={() => { setMetaConfigOpen(false); loadMeta(); }}
      />
    </Box>
  );
}

// ─── Meta Config Dialog ───────────────────────────────────────────────────────

function MetaConfigDialog({
  open,
  config,
  onClose,
  onSaved,
}: {
  open: boolean;
  config: MetaConfig | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    access_token: '',
    ad_account_id: config?.ad_account_id || '',
    page_id: config?.page_id || '',
    ig_user_id: config?.ig_user_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<{ valid: boolean; name?: string; error?: string } | null>(null);

  useEffect(() => {
    if (open && config) {
      setForm((f) => ({
        ...f,
        ad_account_id: config.ad_account_id || '',
        page_id: config.page_id || '',
        ig_user_id: config.ig_user_id || '',
      }));
      setTokenStatus(null);
    }
  }, [open, config]);

  const handleValidate = async () => {
    setValidating(true);
    setTokenStatus(null);
    try {
      // Save token first so server can validate it
      if (form.access_token) {
        await metaService.saveConfig({ access_token: form.access_token });
      }
      const result = await metaService.validateToken();
      setTokenStatus(result.valid
        ? { valid: true, name: result.user?.name }
        : { valid: false, error: result.error }
      );
    } catch (e: any) {
      setTokenStatus({ valid: false, error: e.message });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: any = {
        ad_account_id: form.ad_account_id,
        page_id: form.page_id,
        ig_user_id: form.ig_user_id,
      };
      if (form.access_token) payload.access_token = form.access_token;
      await metaService.saveConfig(payload);
      onSaved();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FacebookIcon color="primary" />
          <InstagramIcon sx={{ color: '#E1306C' }} />
          <Typography fontWeight={700}>Configurar Meta Ads</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          <Alert severity="info">
            Precisa de uma <b>Meta App</b> com permissões <code>ads_read</code> +{' '}
            <code>instagram_basic</code> + <code>pages_read_engagement</code>.<br />
            Use um <b>System User token</b> (não expira) ou renove manualmente a cada 60 dias.
          </Alert>

          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Access Token
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                type="password"
                placeholder={config?.access_token_set ? '••••••••••••• (atual mantido se vazio)' : 'EAAxxxxxxxxxxxxxxx...'}
                value={form.access_token}
                onChange={(e) => { setForm((f) => ({ ...f, access_token: e.target.value })); setTokenStatus(null); }}
              />
              <Button
                variant="outlined"
                onClick={handleValidate}
                disabled={validating || (!form.access_token && !config?.access_token_set)}
                sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
              >
                {validating ? <CircularProgress size={16} /> : 'Validar'}
              </Button>
            </Stack>
            {tokenStatus && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                {tokenStatus.valid
                  ? <><CheckIcon color="success" fontSize="small" /><Typography variant="caption" color="success.main">Token válido — {tokenStatus.name}</Typography></>
                  : <><ErrorIcon color="error" fontSize="small" /><Typography variant="caption" color="error.main">{tokenStatus.error}</Typography></>
                }
              </Stack>
            )}
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Ad Account ID"
            placeholder="act_XXXXXXXXXXXXXXXXX"
            helperText="Encontre em Meta Ads Manager → Definições da Conta"
            value={form.ad_account_id}
            onChange={(e) => setForm((f) => ({ ...f, ad_account_id: e.target.value }))}
          />

          <TextField
            fullWidth
            size="small"
            label="Page ID (Facebook)"
            placeholder="123456789012345"
            helperText="Opcional — para métricas de posts orgânicos"
            value={form.page_id}
            onChange={(e) => setForm((f) => ({ ...f, page_id: e.target.value }))}
          />

          <TextField
            fullWidth
            size="small"
            label="Instagram Business Account ID"
            placeholder="17841400000000000"
            helperText="Opcional — para grid de posts e métricas IG"
            value={form.ig_user_id}
            onChange={(e) => setForm((f) => ({ ...f, ig_user_id: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={16} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Social Ads Tab ───────────────────────────────────────────────────────────

function SocialAdsTab({
  config,
  summary,
  campaigns,
  ig,
  loading,
  error,
  preset,
  onPresetChange,
  onRefresh,
  onConfigure,
  wau,
  windsorIg,
  windsorAds,
  windsorPrevIg,
  windsorPrevAds,
  windsorSyncLog,
  windsorSyncing,
  windsorDays,
  onWindsorDaysChange,
  onWindsorSync,
}: {
  config: MetaConfig | null;
  summary: MetaAdSummary | null;
  campaigns: MetaCampaign[];
  ig: MetaIGData | null;
  loading: boolean;
  error: string | null;
  preset: string;
  onPresetChange: (v: string) => void;
  onRefresh: () => void;
  onConfigure: () => void;
  wau: WAUResult | null;
  windsorIg: WindsorMetricsResult | null;
  windsorAds: WindsorMetricsResult | null;
  windsorPrevIg: WindsorMetricsResult | null;
  windsorPrevAds: WindsorMetricsResult | null;
  windsorSyncLog: WindsorSyncLog[];
  windsorSyncing: boolean;
  windsorDays: number;
  onWindsorDaysChange: (d: number) => void;
  onWindsorSync: () => void;
}) {
  const isConfigured = config?.access_token_set && config?.ad_account_id;

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          A carregar dados da Meta API...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={onRefresh} size="small">Tentar novamente</Button>}>
        {error}
      </Alert>
    );
  }

  if (!config || !isConfigured) {
    return (
      <Stack spacing={3}>
        <Paper sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', borderRadius: 3 }}>
          <Stack alignItems="center" spacing={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FacebookIcon sx={{ fontSize: 48, color: '#1877F2' }} />
              <InstagramIcon sx={{ fontSize: 48, color: '#E1306C' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Integração Meta Ads
              </Typography>
            <Typography color="text.secondary" maxWidth={480}>
              Conecte a sua conta de anúncios Facebook/Instagram para ver reach, impressões, gastos,
              CTR e performance de campanhas — tudo dentro do Pagali Admin.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%', maxWidth: 480 }}>
            {[
              { icon: <VisibilityIcon color="primary" />, label: 'Alcance & Impressões' },
              { icon: <AdsClickIcon color="warning" />, label: 'Cliques & CTR' },
              { icon: <PaymentsIcon color="success" />, label: 'Gastos & CPM' },
            ].map((item) => (
              <Paper key={item.label} variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                {item.icon}
                <Typography variant="caption" display="block" mt={0.5} fontWeight={600}>
                  {item.label}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Button variant="contained" size="large" startIcon={<SettingsIcon />} onClick={onConfigure}>
            Configurar Agora
          </Button>
        </Stack>
      </Paper>
        <WindsorSection
          ig={windsorIg}
          ads={windsorAds}
          prevIg={windsorPrevIg}
          prevAds={windsorPrevAds}
          syncLog={windsorSyncLog}
          syncing={windsorSyncing}
          onSync={onWindsorSync}
          days={windsorDays}
          onDaysChange={onWindsorDaysChange}
          metaPosts={ig}
          wau={wau}
        />
      </Stack>
    );
  }

  const presets = [
    { value: 'last_7d', label: '7 dias' },
    { value: 'last_14d', label: '14 dias' },
    { value: 'last_30d', label: '30 dias' },
    { value: 'last_90d', label: '90 dias' },
    { value: 'this_month', label: 'Este mês' },
    { value: 'last_month', label: 'Mês passado' },
  ];

  const fmt = (v?: number | null, decimals = 0) =>
    v == null ? '—' : v.toLocaleString('pt-PT', { maximumFractionDigits: decimals });

  const statusColor = (s: string) => {
    if (s === 'ACTIVE') return 'success';
    if (s === 'PAUSED') return 'warning';
    if (s === 'ARCHIVED') return 'default';
    return 'default';
  };

  return (
    <Stack spacing={3}>
      {/* Preset selector */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {presets.map((p) => (
          <Chip
            key={p.value}
            label={p.label}
            onClick={() => onPresetChange(p.value)}
            color={preset === p.value ? 'primary' : 'default'}
            variant={preset === p.value ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
          {summary?.date_start && summary?.date_stop
            ? `${summary.date_start} → ${summary.date_stop}`
            : ''}
        </Typography>
      </Stack>

      {/* Summary cards */}
      {summary ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <StatCard
            icon={<PaymentsIcon />}
            label="Gasto total"
            value={`$${fmt(summary.spend, 2)}`}
            color="error"
          />
          <StatCard
            icon={<VisibilityIcon />}
            label="Impressões"
            value={fmt(summary.impressions)}
            color="primary"
          />
          <StatCard
            icon={<TouchAppIcon />}
            label="Cliques"
            value={fmt(summary.clicks)}
            color="info"
            delta={summary.ctr ? summary.ctr : undefined}
            deltaLabel={`CTR ${fmt(summary.ctr, 2)}%`}
          />
          <StatCard
            icon={<AdsClickIcon />}
            label="CPM"
            value={`$${fmt(summary.cpm, 2)}`}
            color="secondary"
          />
        </Box>
      ) : (
        <Alert severity="info">
          Sem dados de publicidade para este período. Certifique-se de que o Ad Account ID está correto e há campanhas ativas.
        </Alert>
      )}

      {/* Campaigns table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#1877F2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FacebookIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} lineHeight={1.2}>Campanhas Facebook Ads</Typography>
              <Typography variant="caption" color="text.secondary">
                {campaigns.length} campanhas · {preset}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campanha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Impressões</TableCell>
                <TableCell align="right">Alcance</TableCell>
                <TableCell align="right">Cliques</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Gasto</TableCell>
                <TableCell align="right">CPM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    Sem campanhas encontradas neste período.
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {c.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.objective?.replace(/_/g, ' ').toLowerCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status}
                        color={statusColor(c.status) as any}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="right">{fmt(c.insights?.impressions)}</TableCell>
                    <TableCell align="right">{fmt(c.insights?.reach)}</TableCell>
                    <TableCell align="right">{fmt(c.insights?.clicks)}</TableCell>
                    <TableCell align="right">{c.insights ? `${fmt(c.insights.ctr, 2)}%` : '—'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {c.insights ? `$${fmt(c.insights.spend, 2)}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{c.insights ? `$${fmt(c.insights.cpm, 2)}` : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Instagram section */}
      {config.ig_user_id && ig && (
        <Box>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <InstagramIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                      Instagram
                      {ig.account?.username && <Typography component="span" variant="caption" color="text.secondary" ml={1}>@{ig.account.username}</Typography>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Últimos 9 posts
                    </Typography>
                  </Box>
                </Stack>
                {ig.account && (
                  <Stack direction="row" spacing={3}>
                    {ig.account.followers_count > 0 && (
                      <Box textAlign="center">
                        <Typography variant="h6" fontWeight={700}>{fmt(ig.account.followers_count)}</Typography>
                        <Typography variant="caption" color="text.secondary">Seguidores</Typography>
                      </Box>
                    )}
                    {ig.account.impressions > 0 && (
                      <Box textAlign="center">
                        <Typography variant="h6" fontWeight={700}>{fmt(ig.account.impressions)}</Typography>
                        <Typography variant="caption" color="text.secondary">Impressões</Typography>
                      </Box>
                    )}
                    {ig.account.reach > 0 && (
                      <Box textAlign="center">
                        <Typography variant="h6" fontWeight={700}>{fmt(ig.account.reach)}</Typography>
                        <Typography variant="caption" color="text.secondary">Alcance</Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Paper>

          {/* Instagram posts grid */}
          {ig.posts.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              {ig.posts.map((post) => (
                <Paper key={post.id} sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                  <Box sx={{ position: 'relative' }}>
                    {post.media_url ? (
                      <>
                        <Box
                          component="img"
                          src={post.media_url}
                          alt={post.caption || ''}
                          sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }}
                        />
                        {post.media_type === 'VIDEO' && (
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', pointerEvents: 'none' }}>
                            <PlayCircleIcon sx={{ fontSize: 44, color: 'white', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }} />
                          </Box>
                        )}
                      </>
                    ) : post.media_type === 'VIDEO' ? (
                      <Box sx={{ width: '100%', aspectRatio: '1 / 1', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <PlayCircleIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.7)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>Vídeo</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ width: '100%', aspectRatio: '1 / 1', backgroundColor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InstagramIcon sx={{ opacity: 0.3, fontSize: 40 }} />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1.5}>
                        <Typography variant="caption">❤️ {fmt(post.like_count)}</Typography>
                        <Typography variant="caption">💬 {fmt(post.comments_count)}</Typography>
                      </Stack>
                      <Tooltip title={post.media_type === 'VIDEO' ? 'Ver vídeo no Instagram' : 'Abrir no Instagram'}>
                        <IconButton size="small" href={post.permalink} target="_blank" rel="noopener">
                          <LaunchIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {post.caption && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {post.caption}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">Sem posts Instagram encontrados.</Typography>
            </Paper>
          )}
        </Box>
      )}

      {config.ig_user_id && !ig && (
        <Alert severity="warning">
          Instagram Business Account ID configurado, mas não foi possível carregar os posts.
          Verifique se o token tem permissões <code>instagram_basic</code>.
        </Alert>
      )}

      {/* ── Secção Windsor — histórico cached (independente da config Meta) ── */}
      <WindsorSection
        ig={windsorIg}
        ads={windsorAds}
        prevIg={windsorPrevIg}
        prevAds={windsorPrevAds}
        syncLog={windsorSyncLog}
        syncing={windsorSyncing}
        onSync={onWindsorSync}
        days={windsorDays}
        onDaysChange={onWindsorDaysChange}
        metaPosts={ig}
        wau={wau}
      />
    </Stack>
  );
}

// ─── Windsor Section ──────────────────────────────────────────────────────────

function WindsorSection({
  ig,
  ads,
  prevIg,
  prevAds,
  syncLog,
  syncing,
  onSync,
  days,
  onDaysChange,
  metaPosts,
  wau,
}: {
  ig: WindsorMetricsResult | null;
  ads: WindsorMetricsResult | null;
  prevIg: WindsorMetricsResult | null;
  prevAds: WindsorMetricsResult | null;
  syncLog: WindsorSyncLog[];
  syncing: boolean;
  onSync: () => void;
  days: number;
  onDaysChange: (d: number) => void;
  metaPosts: MetaIGData | null;
  wau: WAUResult | null;
}) {
  const [igAccFilter, setIgAccFilter] = useState<string | null>(null);
  const [adsAccFilter, setAdsAccFilter] = useState<string | null>(null);

  const lastSync = syncLog[0];
  const fmt = (v?: number | null, dec = 0) =>
    v == null ? '—' : Number(v).toLocaleString('pt-PT', { maximumFractionDigits: dec });

  const delta = (curr: number, prev: number | null | undefined): number | null => {
    if (!prev || prev === 0) return null;
    return Math.round(((curr - prev) / prev) * 1000) / 10;
  };

  const DeltaBadge = ({ pct }: { pct: number | null }) => {
    if (pct === null) return null;
    const up = pct >= 0;
    return (
      <Typography variant="caption" sx={{ color: up ? 'success.main' : 'error.main' }}>
        {up ? '▲' : '▼'} {Math.abs(pct)}%
      </Typography>
    );
  };

  // Filter series by account
  const igSeries = (ig?.series ?? []).filter(r => !igAccFilter || r.account_id === igAccFilter);
  const adsSeries = (ads?.series ?? []).filter(r => !adsAccFilter || r.account_id === adsAccFilter);

  // Unique accounts for selectors
  const igAccounts = [...new Set((ig?.series ?? []).map(r => r.account_id))]
    .map(id => ({ id, name: (ig?.series ?? []).find(r => r.account_id === id)?.account_name ?? id }));
  const adsAccounts = [...new Set((ads?.series ?? []).map(r => r.account_id))]
    .map(id => ({ id, name: (ads?.series ?? []).find(r => r.account_id === id)?.account_name ?? id }));

  // Aggregate filtered totals
  const igTotals = {
    reach: igSeries.reduce((s, r) => s + (r.reach ?? 0), 0),
    views: igSeries.reduce((s, r) => s + (r.views ?? 0), 0),
    new_followers: igSeries.reduce((s, r) => s + (r.new_followers ?? 0), 0),
    interactions: igSeries.reduce((s, r) => s + (r.total_interactions ?? 0), 0),
    likes: igSeries.reduce((s, r) => s + (r.likes ?? 0), 0),
    comments: igSeries.reduce((s, r) => s + (r.comments ?? 0), 0),
    saves: igSeries.reduce((s, r) => s + (r.saves ?? 0), 0),
  };
  const adsTotals = {
    spend: adsSeries.reduce((s, r) => s + Number(r.spend ?? 0), 0),
    impressions: adsSeries.reduce((s, r) => s + (r.impressions ?? 0), 0),
    clicks: adsSeries.reduce((s, r) => s + (r.clicks ?? 0), 0),
    adReach: adsSeries.reduce((s, r) => s + (r.ad_reach ?? 0), 0),
  };

  // Previous period aggregates (unfiltered — same accounts)
  const prevIgTotals = prevIg ? {
    reach: (prevIg.series ?? []).reduce((s, r) => s + (r.reach ?? 0), 0),
    views: (prevIg.series ?? []).reduce((s, r) => s + (r.views ?? 0), 0),
    new_followers: (prevIg.series ?? []).reduce((s, r) => s + (r.new_followers ?? 0), 0),
    interactions: (prevIg.series ?? []).reduce((s, r) => s + (r.total_interactions ?? 0), 0),
  } : null;
  const prevAdsTotals = prevAds ? {
    spend: (prevAds.series ?? []).reduce((s, r) => s + Number(r.spend ?? 0), 0),
    impressions: (prevAds.series ?? []).reduce((s, r) => s + (r.impressions ?? 0), 0),
    clicks: (prevAds.series ?? []).reduce((s, r) => s + (r.clicks ?? 0), 0),
  } : null;

  // Derived metrics
  const engRate = igTotals.reach > 0
    ? Math.round((igTotals.interactions / igTotals.reach) * 10000) / 100
    : null;
  const custoSeg = igTotals.new_followers > 0 && adsTotals.spend > 0
    ? `$${(adsTotals.spend / igTotals.new_followers).toFixed(2)}`
    : null;

  // Chart data
  const igReachChartData = igSeries.map(r => ({
    label: formatShortDate(r.date),
    reach: r.reach ?? 0,
    views: r.views ?? 0,
  }));
  const igEngChartData = igSeries.map(r => ({
    label: formatShortDate(r.date),
    likes: r.likes ?? 0,
    comments: r.comments ?? 0,
    saves: r.saves ?? 0,
  }));
  const adsSpendChartData = adsSeries.map(r => ({
    label: formatShortDate(r.date),
    spend: Number(r.spend ?? 0),
    clicks: r.clicks ?? 0,
  }));
  const adsCtrChartData = adsSeries.map(r => ({
    label: formatShortDate(r.date),
    ctr: Number(r.ctr ?? 0),
    cpc: Number(r.cpc ?? 0),
  }));

  const hasIgData = igSeries.length > 0;
  const hasAdsData = adsSeries.length > 0;
  const hasAnyData = hasIgData || hasAdsData;

  // Alerts
  const reachDelta = prevIgTotals ? delta(igTotals.reach, prevIgTotals.reach) : null;
  const spendDelta = prevAdsTotals ? delta(adsTotals.spend, prevAdsTotals.spend) : null;

  // Top posts from Meta API (sorted by likes — API doesn't return per-post reach)
  const topPosts = metaPosts?.posts
    ? [...metaPosts.posts].sort((a, b) => (b.like_count ?? 0) - (a.like_count ?? 0)).slice(0, 6)
    : [];

  // CSV export
  const exportCsv = () => {
    const rows = [
      ...igSeries.map(r => ({ plataforma: 'instagram', ...r })),
      ...adsSeries.map(r => ({ plataforma: 'meta_ads', ...r })),
    ];
    if (rows.length === 0) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(','), ...rows.map(r => keys.map(k => (r as any)[k] ?? '').join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `windsor_${days}d_${todayCv()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const periodOptions = [
    { d: 7, label: '7d' },
    { d: 14, label: '14d' },
    { d: 30, label: '30d' },
    { d: 60, label: '60d' },
  ];

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2} mt={1} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Tendências Windsor.ai</Typography>
          <Typography variant="caption" color="text.secondary">
            Cache MySQL · actualizado 2×/dia
            {lastSync?.finished_at && <> · sync: {new Date(lastSync.finished_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</>}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          {/* Extension 1 — period selector */}
          {periodOptions.map(p => (
            <Chip key={p.d} label={p.label} size="small"
              onClick={() => onDaysChange(p.d)}
              color={days === p.d ? 'primary' : 'default'}
              variant={days === p.d ? 'filled' : 'outlined'} />
          ))}
          <Button size="small" variant="outlined"
            startIcon={syncing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={onSync} disabled={syncing}>
            {syncing ? 'A sincronizar…' : 'Sincronizar'}
          </Button>
          {/* Extension 9 — export CSV */}
          {hasAnyData && (
            <Button size="small" variant="outlined" onClick={exportCsv}>
              Exportar CSV
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Extension 8 — automatic alerts */}
      {lastSync?.status === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Último sync Windsor com erro: {lastSync.error_msg ?? 'erro desconhecido'}
        </Alert>
      )}
      {reachDelta !== null && reachDelta < -20 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Alcance orgânico caiu {Math.abs(reachDelta)}% face ao período anterior — verifique a frequência de publicação.
        </Alert>
      )}
      {spendDelta !== null && Math.abs(spendDelta) > 50 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Gasto em anúncios variou {spendDelta > 0 ? '+' : ''}{spendDelta.toFixed(1)}% face ao período anterior.
        </Alert>
      )}

      {!hasAnyData ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
          <Typography color="text.secondary" gutterBottom>Sem dados Windsor disponíveis.</Typography>
          <Typography variant="caption" color="text.secondary">
            Configure <code>WINDSOR_API_KEY</code> no servidor e clique em "Sincronizar".
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>

          {/* ── Instagram Orgânico ── */}
          {hasIgData && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              {/* Extension 6 — account selector */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InstagramIcon sx={{ color: '#E1306C', fontSize: 20 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Instagram Orgânico</Typography>
                    <Typography variant="caption" color="text.secondary">últimos {days} dias</Typography>
                  </Box>
                </Stack>
                {igAccounts.length > 1 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    <Chip size="small" label="Todas" onClick={() => setIgAccFilter(null)}
                      color={igAccFilter === null ? 'secondary' : 'default'}
                      variant={igAccFilter === null ? 'filled' : 'outlined'} />
                    {igAccounts.map(a => (
                      <Chip key={a.id} size="small" label={a.name}
                        onClick={() => setIgAccFilter(a.id)}
                        color={igAccFilter === a.id ? 'secondary' : 'default'}
                        variant={igAccFilter === a.id ? 'filled' : 'outlined'} />
                    ))}
                  </Stack>
                )}
              </Stack>

              {/* Extension 4 — delta indicators; Extension 2 — more metrics */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                {[
                  { label: 'Alcance', value: igTotals.reach, prev: prevIgTotals?.reach },
                  { label: 'Views', value: igTotals.views, prev: prevIgTotals?.views },
                  { label: 'Seguidores ganhos', value: igTotals.new_followers, prev: prevIgTotals?.new_followers },
                  { label: 'Interações', value: igTotals.interactions, prev: prevIgTotals?.interactions },
                ].map(({ label, value, prev }) => (
                  <Box key={label} sx={{ textAlign: 'center', p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                    <Typography variant="h6" fontWeight={700}>{fmt(value)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <DeltaBadge pct={delta(value, prev)} />
                  </Box>
                ))}
              </Box>

              {/* Extension 2 — engagement rate; Extension 7 — custo por seguidor */}
              <Stack direction="row" spacing={2} mb={2.5} flexWrap="wrap">
                {engRate !== null && (
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(211, 47, 47, 0.12)', borderRadius: 1.5, textAlign: 'center' }}>
                    <Typography variant="body1" fontWeight={700} color="error.main">{engRate}%</Typography>
                    <Typography variant="caption" color="text.secondary">Taxa de engagement</Typography>
                  </Box>
                )}
                {custoSeg && (
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(46, 125, 50, 0.12)', borderRadius: 1.5, textAlign: 'center' }}>
                    <Typography variant="body1" fontWeight={700} color="success.main">{custoSeg}</Typography>
                    <Typography variant="caption" color="text.secondary">Custo por seguidor</Typography>
                  </Box>
                )}
              </Stack>

              {/* Reach + Views chart */}
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                Alcance & Views
              </Typography>
              <ResponsiveContainer width="100%" height={155}>
                <AreaChart data={igReachChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="igReach" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E1306C" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#E1306C" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="igViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="reach" name="Alcance" stroke="#E1306C" fill="url(#igReach)" strokeWidth={2} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#7c3aed" fill="url(#igViews)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>

              {/* Extension 2 — likes / comments / saves chart */}
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mt={2} mb={0.5}>
                Likes · Comentários · Guardados
              </Typography>
              <ResponsiveContainer width="100%" height={130}>
                <AreaChart data={igEngChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="igLikes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="igComments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="igSaves" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="likes" name="Likes" stroke="#f43f5e" fill="url(#igLikes)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="comments" name="Comentários" stroke="#f59e0b" fill="url(#igComments)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="saves" name="Guardados" stroke="#10b981" fill="url(#igSaves)" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* ── Meta Ads ── */}
          {hasAdsData && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              {/* Extension 6 — account selector */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FacebookIcon sx={{ color: '#1877F2', fontSize: 20 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Meta Ads</Typography>
                    <Typography variant="caption" color="text.secondary">últimos {days} dias</Typography>
                  </Box>
                </Stack>
                {adsAccounts.length > 1 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    <Chip size="small" label="Todas" onClick={() => setAdsAccFilter(null)}
                      color={adsAccFilter === null ? 'primary' : 'default'}
                      variant={adsAccFilter === null ? 'filled' : 'outlined'} />
                    {adsAccounts.map(a => (
                      <Chip key={a.id} size="small" label={a.name}
                        onClick={() => setAdsAccFilter(a.id)}
                        color={adsAccFilter === a.id ? 'primary' : 'default'}
                        variant={adsAccFilter === a.id ? 'filled' : 'outlined'} />
                    ))}
                  </Stack>
                )}
              </Stack>

              {/* Extension 4 — delta indicators */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2.5 }}>
                {[
                  { label: 'Gasto', value: adsTotals.spend, prev: prevAdsTotals?.spend, prefix: '$', dec: 2 },
                  { label: 'Impressões', value: adsTotals.impressions, prev: prevAdsTotals?.impressions, dec: 0 },
                  { label: 'Cliques', value: adsTotals.clicks, prev: prevAdsTotals?.clicks, dec: 0 },
                  { label: 'Alcance pago', value: adsTotals.adReach, dec: 0 },
                ].map(({ label, value, prev, prefix = '', dec = 0 }) => (
                  <Box key={label} sx={{ textAlign: 'center', p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                    <Typography variant="h6" fontWeight={700}>{prefix}{fmt(value, dec)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    {prev !== undefined && <DeltaBadge pct={delta(value, prev)} />}
                  </Box>
                ))}
              </Box>

              {/* Gasto + Cliques chart */}
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                Gasto & Cliques
              </Typography>
              <ResponsiveContainer width="100%" height={155}>
                <AreaChart data={adsSpendChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="adsSpend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1877F2" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#1877F2" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="adsClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="spend" name="Gasto ($)" stroke="#1877F2" fill="url(#adsSpend)" strokeWidth={2} />
                  <Area type="monotone" dataKey="clicks" name="Cliques" stroke="#f59e0b" fill="url(#adsClicks)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>

              {/* Extension 3 — CTR + CPC trend chart */}
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mt={2} mb={0.5}>
                CTR (%) & CPC ($)
              </Typography>
              <ResponsiveContainer width="100%" height={130}>
                <LineChart data={adsCtrChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="ctr" name="CTR (%)" stroke="#6366f1" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="cpc" name="CPC ($)" stroke="#ec4899" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Extension 5 — top posts por likes (Meta API) */}
          {topPosts.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <InstagramIcon sx={{ color: '#E1306C', fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Top Posts</Typography>
                  <Typography variant="caption" color="text.secondary">ordenados por likes · últimos posts</Typography>
                </Box>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {topPosts.map((post, i) => (
                  <Box key={post.id ?? i} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ position: 'relative' }}>
                      {post.media_url ? (
                        <>
                          <Box
                            component="img"
                            src={post.media_url}
                            alt={post.caption?.slice(0, 40) ?? 'Post'}
                            sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                            onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                          />
                          {post.media_type === 'VIDEO' && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', pointerEvents: 'none' }}>
                              <PlayCircleIcon sx={{ fontSize: 40, color: 'white', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }} />
                            </Box>
                          )}
                        </>
                      ) : post.media_type === 'VIDEO' ? (
                        <Box sx={{ width: '100%', aspectRatio: '1', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PlayCircleIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.7)' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>Vídeo</Typography>
                        </Box>
                      ) : null}
                    </Box>
                    <Box sx={{ p: 1.25 }}>
                      <Stack direction="row" spacing={1.5}>
                        <Typography variant="caption" color="text.secondary">❤ {(post.like_count ?? 0).toLocaleString('pt-PT')}</Typography>
                        <Typography variant="caption" color="text.secondary">💬 {(post.comments_count ?? 0).toLocaleString('pt-PT')}</Typography>
                      </Stack>
                      {post.permalink && (
                        <Box mt={0.5}>
                          <a href={post.permalink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#E1306C', textDecoration: 'none' }}>
                            {post.media_type === 'VIDEO' ? '▶ Ver vídeo ↗' : 'Ver ↗'}
                          </a>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* ── Correlação WAU × IG Orgânico ── */}
          {(() => {
            if (!wau || !hasIgData) return null;
            const corrData = wau.semanas.map(w => {
              const weekStart = new Date(w.semana_inicio);
              const weekEnd = new Date(weekStart);
              weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
              const weekReach = igSeries
                .filter(r => { const d = new Date(r.date); return d >= weekStart && d <= weekEnd; })
                .reduce((s, r) => s + (r.reach ?? 0), 0);
              return { label: formatShortDate(w.semana_inicio), wau: w.wau, ig_reach: weekReach };
            }).filter(d => d.ig_reach > 0 || d.wau > 0);
            if (corrData.length < 2) return null;
            return (
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Correlação WAU × IG Orgânico</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Barras = utilizadores activos Pagali por semana · Linha = alcance IG semanal
                    </Typography>
                  </Box>
                </Stack>
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={corrData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="wau" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <YAxis yAxisId="ig" orientation="right" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Bar yAxisId="wau" dataKey="wau" name="WAU" fill="#7c3aed" opacity={0.75} radius={[3, 3, 0, 0]} />
                    <Line yAxisId="ig" type="monotone" dataKey="ig_reach" name="Alcance IG" stroke="#E1306C" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Paper>
            );
          })()}

        </Stack>
      )}
    </Box>
  );
}

function MetricRow({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ color }}>
        {value.toLocaleString('pt-PT')}
      </Typography>
    </Stack>
  );
}

function ProgressBar({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight={700}>{value}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, value))}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
}

function ConversionCard({
  icon,
  title,
  color,
  hasData,
  emptyText,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  hasData: boolean;
  emptyText: string;
  children?: React.ReactNode;
}) {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            backgroundColor: `${color}.main`,
            color: `${color}.contrastText`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
      </Stack>
      {hasData ? children : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography color="text.secondary" variant="body2">{emptyText}</Typography>
        </Box>
      )}
    </Paper>
  );
}

// ─── Resumo Diário Tab ────────────────────────────────────────────────────────

function ResumoDiarioTab({
  resumo,
  loading,
  error,
  onRefresh,
}: {
  resumo: { text: string; gerado_em: string; meta: any } | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          A analisar dados e gerar resumo com IA…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRefresh}>Tentar novamente</Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!resumo) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <AutoAwesomeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary" mb={3}>
          Clica em <b>Gerar Resumo</b> para obter a análise semanal com IA.
        </Typography>
        <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={onRefresh}>
          Gerar Resumo
        </Button>
      </Box>
    );
  }

  // Parse markdown sections: split on "## " headings
  const sections = resumo.text.split(/\n(?=## )/).filter(Boolean);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" color="text.secondary">
            Gerado a {new Date(resumo.gerado_em).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Stack>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
          Regenerar
        </Button>
      </Stack>

      {resumo.meta && (
        <Box mb={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={resumo.meta.fontes_erros?.length > 0 ? 1 : 0}>
            {resumo.meta.tem_dados_ads && <Chip label="Meta Ads ✓" size="small" color="primary" variant="outlined" />}
            {resumo.meta.tem_dados_ig_api && <Chip label="Instagram API ✓" size="small" color="secondary" variant="outlined" />}
            {resumo.meta.tem_dados_ig_windsor && <Chip label="IG Windsor ✓" size="small" color="info" variant="outlined" />}
            {resumo.meta.tem_dados_wau && <Chip label="WAU ✓" size="small" color="success" variant="outlined" />}
            {resumo.meta.registos_semana > 0 && (
              <Chip label={`${resumo.meta.registos_semana} registos`} size="small" color="warning" variant="outlined" />
            )}
          </Stack>
          {resumo.meta.fontes_erros?.length > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                Fontes com erro (dados incompletos): {resumo.meta.fontes_erros.join(' · ')}
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        {sections.map((section, i) => {
          const lines = section.trim().split('\n');
          const heading = lines[0].replace(/^##\s*/, '').trim();
          const bodyLines = lines.slice(1).join('\n').trim().split('\n');
          return (
            <Paper key={i} sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                {heading}
              </Typography>
              <Box>
                {bodyLines.map((line, j) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <Box key={j} sx={{ height: 6 }} />;
                  const bulletMatch = trimmed.match(/^[-*]\s+(.+)/);
                  if (bulletMatch) {
                    return (
                      <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, lineHeight: 1.65 }}>•</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                          {renderInlineMd(bulletMatch[1])}
                        </Typography>
                      </Box>
                    );
                  }
                  const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
                  if (numMatch) {
                    return (
                      <Box key={j} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0, lineHeight: 1.65 }}>{numMatch[1]}.</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                          {renderInlineMd(numMatch[2])}
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Typography key={j} variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 0.5 }}>
                      {renderInlineMd(trimmed)}
                    </Typography>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Funil de Activação ───────────────────────────────────────────────────────

function FunilCard({ funil }: {
  funil: {
    semana_atual: {
      registos: number; kyc_aprovado: number; kyc_pendente: number;
      primeira_transacao: number; taxa_kyc_pct: number | null; taxa_activacao_pct: number | null;
    };
    historico_firestore: any[];
  };
}) {
  const { semana_atual: s } = funil;

  const steps = [
    { label: 'Registos', value: s.registos, color: '#7c3aed', pct: 100 },
    {
      label: 'KYC Aprovado',
      value: s.kyc_aprovado,
      color: '#6d28d9',
      pct: s.registos > 0 ? Math.round((s.kyc_aprovado / s.registos) * 100) : 0,
      rate: s.taxa_kyc_pct,
      rateLabel: 'taxa KYC',
    },
    {
      label: 'Primeira Transação',
      value: s.primeira_transacao,
      color: '#84cc16',
      pct: s.registos > 0 ? Math.round((s.primeira_transacao / s.registos) * 100) : 0,
      rate: s.taxa_activacao_pct,
      rateLabel: 'taxa activação',
    },
  ];

  const hasHistorico = funil.historico_firestore.length > 0;
  const historicoData = funil.historico_firestore.slice(-8).map((h: any) => ({
    label: h.week ?? '?',
    kyc: h.kyc_aprovado ?? 0,
    tx: h.primeira_transacao ?? 0,
    wau: h.wau_users ?? 0,
  }));

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Funil de Activação</Typography>
          <Typography variant="caption" color="text.secondary">
            Esta semana · registos → KYC → primeira transação
          </Typography>
        </Box>
      </Stack>

      {s.registos === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sem registos esta semana ainda. Os dados aparecem quando o funil tracking estiver activo.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {steps.map((step) => (
            <Box key={step.label}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
                  {step.rate != null && (
                    <Chip
                      label={`${step.rate.toFixed(1)}% ${step.rateLabel}`}
                      size="small"
                      sx={{
                        fontSize: 10,
                        height: 20,
                        bgcolor: step.rate >= 50 ? 'success.light' : step.rate >= 20 ? 'warning.light' : 'error.light',
                        color: step.rate >= 50 ? 'success.dark' : step.rate >= 20 ? 'warning.dark' : 'error.dark',
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" fontWeight={700} sx={{ color: step.color }}>
                  {step.value.toLocaleString('pt-PT')}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, step.pct)}
                sx={{
                  height: 10,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': { bgcolor: step.color, borderRadius: 1 },
                }}
              />
            </Box>
          ))}

          {s.kyc_pendente > 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <b>{s.kyc_pendente}</b> utilizadores com KYC pendente esta semana — potencial de activação imediato.
              </Typography>
            </Alert>
          )}
        </Stack>
      )}

      {hasHistorico && (
        <Box mt={3}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
            Histórico semanal (Firestore)
          </Typography>
          <ResponsiveContainer width="100%" height={130}>
            <ComposedChart data={historicoData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <RechartsTooltip />
              <Bar dataKey="kyc" name="KYC aprovado" fill="#7c3aed" opacity={0.8} radius={[3, 3, 0, 0]} />
              <Bar dataKey="tx" name="Primeira tx" fill="#84cc16" opacity={0.8} radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="wau" name="WAU" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}

function renderInlineMd(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}
